import React from 'react';
import Dropzone from 'react-dropzone';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Field, getFormValues, reduxForm} from 'redux-form';

import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Fab from '@mui/material/Fab';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import CloseIcon from '@mui/icons-material/Close';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import {Typography} from '@mui/material';
import Link from '@mui/material/Link';
import FormHelperText from '@mui/material/FormHelperText';

import {commonOperations} from './../../../reducks/common';
import withRouter from '../withRouter';
import {withMediaQuery} from '../../atoms/withMediaQuery';
import {renderTextField} from '../../atoms/CustomPart';
import {executeFileDownload} from '../../../common/commonGmn';
import {getShootingDateFromExif} from '../../../common/fileOperations';
import * as validateRule from '../../validateRule';

/**
 * @typedef UploadFileInfo アップロードファイル情報
 * @property {string} contentDocumentId BlobStorage上のファイルパス。
 * @property {string} fileName 拡張子付きファイル名。
 * @property {string} fileBody dataスキーム付きBase64形式データ。
 * @property {string} contentType Content-Type
 * @property {File} [fileData] アップロードするファイルの実体。未保存のファイルのみ。
 * @property {object} [anchor] ダウンロードリンク。保存済のファイルのみ。
 * @property {string} [date] 撮影日。写真添付の場合のみ。
 */

/**
 * ファイルアップロード
 */
class Upload extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      files: [],
      dialog: false,
      reload: false,
    };

    this.image = null;

    this._initialize();
  }

  /**
   * データの初期化。
   */
  _initialize = () => {
    const {
      componentId,
      downloadHandler,
      doShowMessage,
      initFile,
    } = this.props;

    /** @type UploadFileInfo[] */
    this.files = [];

    /** @type UploadFileInfo[] */
    this.deleteFiles = [];

    let index = 1;
    for (const row of initFile) {
      const emptyInfo = {
        contentDocumentId: null,
        fileName: '',
        fileBody: null,
      };

      const _row = Object.assign(emptyInfo, row);

      if (!_row.contentDocumentId) {
        _row.contentDocumentId = index++;
      }

      // ダウンロード用関数が設定されている場合はonClickイベントに設定
      if (downloadHandler) {
        _row.anchor = {
          onClick: async (event) => {
            try {
              const response = await downloadHandler(componentId, _row);
              executeFileDownload(response);
            } catch (err) {
              console.error(err);

              doShowMessage({
                message: {
                  id: 'BFY000051E',
                  values: ['ファイル', 'ダウンロード'],
                },
              });
            }
          },
        };
      }

      // プレビュー取得を非同期で実行
      this._setImgSrc(componentId, _row);

      this.files.push(_row);
    }
  };

  /**
   * 画像ファイルの取得と項目への設定。
   *
   * @param {string} componentId コンポーネントID
   * @param {object} fileInfo ファイル情報
   */
  _setImgSrc = async (componentId, fileInfo) => {
    const {
      previewHandler,
    } = this.props;

    // プレビューを行わない場合は処理終了
    if (!previewHandler) {
      return;
    }

    try {
      const response = await previewHandler(componentId, fileInfo);

      // 取得したBlobファイルをBase64に変換して設定する
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        fileInfo.fileBody = base64;

        // コンポーネント更新のため取得したファイル名を設定
        this.setState({fileName: componentId + '-' + fileInfo.fileName});
      };

      reader.readAsDataURL(response.data);
    } catch (err) {
      // エラーでもメッセージは表示しない
      console.error(err);
      fileInfo.fileBody = '';
    }
  };

  /**
   * エラーメッセージをまとめて表示
   * @param {string} msgId
   * @param {array<string>} fileNames
   * @param {string} option
   */
  setErr = (msgId, fileNames, option) => {
    // fileNamesは以下の形式にする
    // ['ファイル名1','ファイル名2', ...]
    this.clearFile();

    const messages = [];
    if (fileNames) {
      for (const fileName of fileNames) {
        messages.push({
          id: msgId,
          values: option != null ? [fileName, option] : [fileName],
        });
      }
    } else {
      messages.push({
        id: msgId,
      });
    }

    this.props.doShowMessage({message: messages});
  };

  /**
   * 親コンポーネントへの情報返却
   * @param {object} files 登録ファイルリスト
   * @param {object} deleteFiles 削除ファイルリスト
   */
  returnFile = (files, deleteFiles) => {
    const {componentId, fileSetHandler} = this.props;
    if (fileSetHandler) {
      fileSetHandler(files, deleteFiles, componentId);
      this.setState({reload: true});
    }
  };

  /**
   * 選択ファイル読み込み
   * @param {File} file 選択ファイル
   * @param {string} contentDocumentId ファイルアップロード済の場合、ContentVersionのIdを渡す
   */
  pushFile = async (file, contentDocumentId) => {
    const reader = new FileReader();
    reader.fileName = file.name;

    reader.onload = async (e) => {
      const contentType = (e.target.result).split(';')[0].split(':')[1];
      const newFile = {
        'fileBody': e.target.result,
        'fileName': e.target.fileName,
        'contentType': contentType,
        'contentDocumentId': contentDocumentId,
        'fileData': file,
        'shootingDate': null,
      };

      // 撮影日を表示する場合、EXIFから撮影日を取得する
      if (this.props.showShootingDate) {
        const {
          change,
          componentId,
        } = this.props;

        const index = this.files.length;

        // Refux-Formの項目判別用名称を生成
        const shootingDateName = this._createItemName(componentId, index);

        const shootingDate = await getShootingDateFromExif(file);
        newFile.shootingDate = shootingDate;

        // Redux-Formを使用して値を設定
        change(shootingDateName, shootingDate);
      }

      const createFiles = this.files.concat(newFile);
      this.files = createFiles;
      this.returnFile(this.files, this.deleteFiles);
    };
    reader.readAsDataURL(file);
    // input:fileの情報をクリア
    this.clearFile();
  };

  /**
   * 写真項目名の生成。
   *
   * 撮影日のフォーム値を変更するための判別に使用。
   * @param {string} componentId コンポーネントID
   * @param {number} index インデックス
   * @return {string} 写真項目名
   */
  _createItemName = (componentId, index) => {
    return `${componentId}-shootingDate-${index}`;
  };

  /**
   * 選択ファイルの選択解除
   * @param {object} e
   */
  delFile = (e) => {
    const files = this.files;
    const delFiles = this.deleteFiles;
    const createFiles = [];
    files.forEach((file) => {
      if (file !== e) createFiles.push(file);
      if (file === e && e.contentDocumentId) {
        delFiles.push(e);
      }
    });
    this.files = createFiles;
    this.deleteFiles = delFiles;
    this.returnFile(this.files, this.deleteFiles);
  };

  /**
   * input:fileの情報をクリア
   * @param {object} e
   */
  clearFile = (e) => {
    document.getElementById('btn_addfile_' + this.props.componentId).value = '';
  };

  /**
   * イメージデータのプレビュー
   * @param {object} e
   */
  viewFile = (e) => {
    this.image = e;
    this.setState({dialog: true});
  };

  /**
   * ダイアログのクローズ
   * @param {object} e
   */
  handleClose = (e) => {
    this.image = null;
    this.setState({dialog: false});
  };

  /**
   * ファイルドロップ時の処理
   * @param {array} acceptedFiles
   */
  onDrop = async (acceptedFiles) => {
    const files = acceptedFiles;

    // 添付ファイル数が最大ファイル数を超える場合、エラー
    if ((files.length + this.files.length) >
            this.props.maxFileCount) {
      this.setErr('BFY000057E', null);
      return;
    }

    // カスタムチェック処理
    const error = this.customCheck(files);
    if (error != null) {
      this.setErr(error.id, error.values);
      return;
    }

    // カスタム警告処理
    await this.customWarning(files);

    const sizeErrFiles = [];
    for (const file of files) {
      if (file.size > this.props.maxFileSize) {
        sizeErrFiles.push(file.name);
      } else {
        await this.pushFile(file);
      }
    }
    if (sizeErrFiles.length > 0) {
      const maxSize = this.props.maxFileSize ?
       Math.floor(this.props.maxFileSize / 1000000) : 0;
      this.setErr('BFY000028E', sizeErrFiles, maxSize);
    }
  };

  /**
   * カスタムチェック
   * @param {*} files
   * @return {object} エラー
   */
  customCheck = (files) => {
    const {customChecks} = this.props;
    let result = null;
    if (customChecks == null) {
      return result;
    }
    for (const check of customChecks) {
      const error = check(files, this.files);
      if (error != null) {
        result = error;
        break;
      }
    }
    return result;
  };

  /**
   * カスタム警告
   * @param {object[]} files ファイル情報一覧
   * @return {Promise<string|null>} 警告
   */
  customWarning = async (files) => {
    const {
      customWarnings,
      doShowMessage,
    } = this.props;

    if (customWarnings == null) {
      return null;
    }

    for (const check of customWarnings) {
      const warn = await check(files);
      if (warn) {
        doShowMessage({message: warn});
        break;
      }
    }
  };

  /**
   * DropZoneに渡すMINE TYPEを作成
   * @param {string} acceptFileType
   * @return {object|null} MINE TYPE
  */
  createAcceptedFileType = (acceptFileType) => {
    let acceptObj = {};
    if (!acceptFileType || acceptFileType.length <= 0) {
      return acceptObj;
    }
    for (const fileExtension of acceptFileType.split(',')) {
      let _fileExtension = fileExtension.trim();
      let mineType = this.selectMineType(_fileExtension);
      if (mineType == 'image/jpeg') {
        if (Object.hasOwn(acceptObj, 'image/jpeg')) {
          acceptObj[mineType].push(_fileExtension);
        } else {
          acceptObj[mineType] = [_fileExtension];
        }
      } else {
        acceptObj[mineType] = [_fileExtension];
      }
    }
    return acceptObj;
  };

  /**
   * mineType取得
   * @param {string} fileExtension 拡張子
   * @return {string} minetype
   */
  selectMineType = (fileExtension) => {
    let mineType = '';
    if (fileExtension == '.pdf') {
      mineType = 'application/pdf';
    }
    if (fileExtension == '.doc') {
      mineType = 'application/msword';
    }
    if (fileExtension == '.docx') {
      mineType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    }
    if (fileExtension == '.xls') {
      mineType = 'application/vnd.ms-excel';
    }
    if (fileExtension == '.xlsx') {
      mineType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    }
    if (fileExtension == '.jpg' || fileExtension == '.jpeg') {
      mineType = 'image/jpeg';
    }
    return mineType;
  };

  /**
   * 画面描画
   * @param {bool} _disabled disabled
   * @return {object} オブジェクト
   */
  viewDropzone = (_disabled) => {
    if (this.props.disabled) {
      return <></>;
    }
    return (
      <Box style={{padding: '5px', width: '100%', borderStyle: 'dashed'}} border={1} borderColor="grey.500" borderRadius="8px">
        <Dropzone
          onDropRejected={(fileRejections) => {
            const files = fileRejections;
            const fileNames = [];
            for (const obj of files) {
              const file = obj.file;
              if (file.name != '') {
                fileNames.push(file.name);
              }
            }
            if (fileNames.length > 0) {
              this.setErr('BFY000056E', fileNames);
            }
          }}
          onDrop={this.onDrop}
          accept={this.createAcceptedFileType(this.props.acceptFileType)}
          disabled={_disabled}
        >
          {({getRootProps, getInputProps}) => (
            <section>
              <div {...getRootProps()}>
                <input
                  {...getInputProps()}
                  id={'btn_addfile_' + this.props.componentId} />
                <Stack direction="row" spacing={2} alignItems="center">
                  <Fab
                    id="add"
                    color="primary"
                    aria-label="add"
                    size="small"
                    disabled={_disabled}
                    sx={{ flexShrink: 0 }}
                  >
                    <AddIcon />
                  </Fab>
                  <Typography variant="caption">
                    ここにファイルをドラッグアンドドロップするか、
                    ここをクリックしてファイルを選択してください。
                  </Typography>
                </Stack>
              </div>
            </section>
          )}
        </Dropzone>
      </Box>
    );
  };

  /**
   * 更新処理
   * @param {*} prevProps 前のprops
   */
  componentDidUpdate(prevProps) {
    const isChanged =
      this.isChangedInitFile(this.props.initFile, prevProps.initFile);
    if (!isChanged) {
      return;
    }

    this._initialize();

    this.setState({files: this.files});
  }

  /**
   * 初期ファイルが変わっているか確認
   * @param {array} currnetFile 現在のファイル
   * @param {array} prevFile 前回描画時のファイル
   * @return {bool} true:変更あり, false:変更なし
   */
  isChangedInitFile = (currnetFile, prevFile) => {
    if (prevFile == null) {
      return true;
    }
    if (currnetFile.length != prevFile.length) {
      return true;
    }

    let isSame = true;
    for (let index = 0; index < currnetFile.length; index++) {
      isSame =
        (currnetFile[index].contentDocumentId ===
            prevFile[index].contentDocumentId) &&
        (currnetFile[index].contentType ===
            prevFile[index].contentType) &&
        (currnetFile[index].fileName ===
              prevFile[index].fileName);
      if (!isSame) {
        break;
      }
    }

    return !isSame;
  };

  render() {
    const {
      componentId,
      customWarnings,
      disabled,
      doShowMessage,
      previewHandler,
      required,
      showShootingDate,
    } = this.props;

    const files = this.files;
    const _disabled = disabled ||
      (files.length >= this.props.maxFileCount);
    const preview = files.map((file, index) => {
      const disableShootingDate = disabled || file.contentDocumentId != null;
      const shootingDateName = this._createItemName(componentId, index);

      return (
        <React.Fragment key={`${componentId}-preview-${index}`}>
          <table width="100%" border="0" cellPadding="0" cellSpacing="0">
            <tbody>
              {previewHandler &&
              <tr>
                <td width={this.props.previewWidth}>
                  {
                    file.fileBody != null &&
                    <img width="auto" height="300px" src={file.fileBody} alt={file.fileName}
                      style={{cursor: 'pointer'}}
                      onMouseDown={()=>{
                        this.viewFile(file);
                      }}
                    />
                  }

                  {
                    file.fileBody == null &&
                    <CircularProgress size={50} />
                  }

                </td>
                <td width="65%">&nbsp;</td>
              </tr>
              }
              <tr>
                <td colSpan="2">
                  <IconButton color="primary" aria-label="delete" size="medium" disabled={this.props.disabled}
                    onClick={()=>{
                      this.delFile(file);
                    }}>
                    <DeleteIcon />
                  </IconButton>
                  {/* 未アップロードの項目 */}
                  {!Object.hasOwn(file, 'anchor') &&
                  <Link
                    href={file.fileBody}
                    download={file.fileName}
                    target="_blank"
                    rel="noopener"
                    underline="hover">
                    {file.fileName}
                  </Link>
                  }

                  {/* アップロード済みの項目 */}
                  {Object.hasOwn(file, 'anchor') &&
                  file.contentDocumentId &&
                  <button
                    type="button"
                    className='bf-buttonLink'
                    key={file.fileName}
                    {...file.anchor}
                  >
                    {file.fileName}
                  </button>
                  }
                </td>
              </tr>

              {showShootingDate &&
                <tr>
                  <td colSpan="2">
                    <span>撮影日:&nbsp;</span>
                    {!disableShootingDate &&
                    <Field
                      name={shootingDateName}
                      key={shootingDateName}
                      component={renderTextField}
                      style={{marginBottom: 4, marginTop: -8}}
                      type="date"
                      value={file.shootingDate}
                      defaultValue={file.shootingDate}
                      validate={[
                        validateRule.required,
                        validateRule.futureDate,
                      ]}
                      onBlur={(event, date) => {
                        file.shootingDate = date;

                        // カスタム警告処理がある場合は実行
                        if (customWarnings) {
                          for (const check of customWarnings) {
                            check([date]).then((warn) => {
                              if (warn) {
                                doShowMessage({message: warn});
                              }
                            });
                          }
                        }
                      }}
                    />
                    }
                    {disableShootingDate &&
                      <span>
                        {file.shootingDate?.replaceAll('-', '/')}
                      </span>
                    }
                  </td>
                </tr>
              }

            </tbody>
          </table>
        </React.Fragment>
      );
    });
    return (
      <div>
        {!this.props.disabled &&
        required === true && files.length == 0 &&
          <FormHelperText error>{'必須入力です'}</FormHelperText>
        }
        {preview}
        <div>
          {this.viewDropzone(_disabled)}
        </div>

        <Dialog
          open={this.state.dialog}
          onClose={this.handleClose}
          fullWidth={true}
          maxWidth="xl"
          scroll="paper"
          aria-labelledby="scroll-dialog-title"
        >
          {(this.image) &&
            <React.Fragment>
              <DialogTitle id="scroll-dialog-title">
                {this.image.fileName}
                <IconButton
                  style={{position: 'absolute', right: '0px', top: '0px', color: '#a5a5a5'}}
                  onClick={this.handleClose} size="large">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  <img width="100%" src={this.image.fileBody} alt={this.image.fileName} />
                </DialogContentText>
              </DialogContent>
            </React.Fragment>
          }
        </Dialog>
      </div>
    );
  }
}


Upload.propTypes = {
  classes: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  componentId: PropTypes.string.isRequired, // アップロードコンポーネントを一意に判別するためのID
  customChecks: PropTypes.array,        // カスタムチェックメソッド一覧
  customWarnings: PropTypes.array,      // カスタム警告メソッド一覧
  maxFileCount: PropTypes.number,       // 1度にアップロード可能なファイル数
  maxFileSize: PropTypes.number,        // 1ファイルあたりの許容サイズ
  previewWidth: PropTypes.string,       // プレビューイメージの表示幅
  acceptFileType: PropTypes.string,     // 許可する拡張子を「.（ドット）」込みでカンマ区切りで指定 ex）.jpg,.pdf
  fileSetHandler: PropTypes.func,        // 選択ファイルを格納するための関数
  initFile: PropTypes.array,
  required: PropTypes.bool,
  input: PropTypes.shape({
    onChange: PropTypes.func.isRequired,
  }),
  showShootingDate: PropTypes.bool,     // 撮影日機能を使用するかどうか
  downloadHandler: PropTypes.func,      // ファイルダウンロードを実行するための関数
  previewHandler: PropTypes.func,       // プレビュー画像を取得するための関数。指定時にプレビュー有となる
};

Upload.defaultProps = {
  componentId: 'default',
  maxFileCount: 3,
  maxFileSize: 1000000,
  previewHandler: null,
  acceptFileType: '.txt',
  initFile: [],
  disabled: false,
};

Upload.propTypes = {
  change: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  userInfo: PropTypes.object,
  doShowMessage: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  userInfo: state.auth.userInfo,
});

const mapDispatchToProps = {
  doShowMessage: commonOperations.doShowMessage,
};

const FORM_NAME = 'UPLOADCUSTOMFORM';

export default createComponentWithFormName(FORM_NAME);

/**
 * 指定したフォーム名でコンポーネントを生成
 *
 * @param {string} formName フォーム名
 * @return {ConnectedComponent} Uploadコンポーネント
 */
export function createComponentWithFormName(formName) {
  const Component = reduxForm({
    form: formName,
    destroyOnUnmount: false,
    enableReinitialize: true,
  })(connect((state) => {
    return {
      values: getFormValues(formName)(state),
    };
  })(Upload));

  return connect(
      mapStateToProps,
      mapDispatchToProps,
  )(withRouter(withMediaQuery()(Component)));
}
